/**
 * 8시간 안 쓰면 로그아웃 — 화면 쪽 (v2.231.0 · 점검 F-S7 · 사용자 결정 2026-10-06).
 *
 * 왜 화면에서도 재나
 *   서버는 「이 세션으로 마지막 요청이 온 시각」으로 8시간을 잰다(backend lib/auth/session-state.ts).
 *   그런데 창을 열어 두면 결재 배지가 5분마다, 화면 자료가 1시간마다 저절로 서버를 부르므로
 *   서버 시각만으로는 「사람이 안 쓴다」를 영영 못 잰다(자리를 비운 PC 가 로그인된 채 남는다).
 *   그래서 **사람의 입력**(누르기·자판·휠·터치)이 8시간 없으면 화면이 스스로 로그아웃한다.
 *
 * · 마지막 입력 시각은 localStorage 에 두어 **여러 탭이 함께** 쓴다(한 탭에서 일하면 다른 탭도 안 끊긴다).
 * · 노트북을 덮었다 열면 타이머가 멈춰 있으므로, 화면이 다시 보이는 순간(visibilitychange)에도 잰다.
 * · 저장소를 못 쓰는 창(사생활 보호 등)은 이 탭의 메모리 값만으로 잰다.
 */

import { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth-context';

export const IDLE_LOGOUT_MS = 8 * 60 * 60 * 1000;
const KEY = 'sm.lastActivityAt';
/** 저장은 30초에 한 번만 — 마우스를 누를 때마다 쓰지 않는다 */
const WRITE_EVERY_MS = 30_000;
const CHECK_EVERY_MS = 60_000;
const EVENTS = ['pointerdown', 'keydown', 'wheel', 'touchstart'] as const;

function readStored(): number {
  try {
    const v = Number(window.localStorage.getItem(KEY));
    return Number.isFinite(v) ? v : 0;
  } catch {
    return 0;
  }
}

function writeStored(t: number): void {
  try {
    window.localStorage.setItem(KEY, String(t));
  } catch {
    /* 저장소를 못 쓰는 창 — 메모리 값만으로 잰다 */
  }
}

export function IdleLogout() {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  // 로그아웃 때 돌아올 주소 — 주소가 바뀔 때마다 타이머를 다시 걸지 않으려고 ref 로
  const locRef = useRef(location);
  locRef.current = location;
  const userId = user?.userId ?? null;

  useEffect(() => {
    if (!userId) return;
    let memLast = Date.now();
    let lastWrite = 0;
    let done = false;

    const mark = () => {
      const now = Date.now();
      memLast = now;
      if (now - lastWrite > WRITE_EVERY_MS) {
        lastWrite = now;
        writeStored(now);
      }
    };
    // 로그인했거나 화면을 새로 연 순간도 「쓴 것」으로 본다
    mark();

    const check = () => {
      if (done) return;
      const last = Math.max(memLast, readStored());
      if (Date.now() - last <= IDLE_LOGOUT_MS) return;
      done = true;
      void signOut().then(() => {
        const loc = locRef.current;
        navigate('/login', {
          replace: true,
          state: { idleLogout: true, from: { pathname: loc.pathname, search: loc.search, hash: loc.hash } },
        });
      });
    };
    const onVisible = () => {
      if (document.visibilityState === 'visible') check();
    };

    for (const ev of EVENTS) window.addEventListener(ev, mark, { passive: true, capture: true });
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', check);
    const timer = window.setInterval(check, CHECK_EVERY_MS);
    return () => {
      for (const ev of EVENTS) window.removeEventListener(ev, mark, { capture: true });
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', check);
      window.clearInterval(timer);
    };
  }, [userId, signOut, navigate]);

  return null;
}
